import React, { useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DatePickerWithRange } from "@/components/FormInputs/DatePickerWithRange";
import { DateRange } from "react-day-picker";
import { Menubar, MenubarMenu, MenubarSeparator, MenubarTrigger } from "../ui";
import {
  IntervalSelector,
  CompareToSelector,
  DataIntervalSelector,
  FilterButton,
} from "./components";
import { ChartTopFiltersProps, IntervalPreset } from "./types";

export const ChartTopFilters: React.FC<ChartTopFiltersProps> = ({
  dateRange,
  selectedPreset,
  interval,
  compareTo,
  filtersCount = 0,
  onPresetChange,
  onDateRangeChange,
  onIntervalChange,
  onCompareToChange,
}) => {
  const { t } = useTranslation();
  const datePickerRef = useRef<{ openPicker: () => void }>(null);
  const shouldOpenPickerRef = useRef(false);

  useEffect(() => {
    if (selectedPreset === "custom" && shouldOpenPickerRef.current) {
      datePickerRef.current?.openPicker();
      shouldOpenPickerRef.current = false;
    }
  }, [selectedPreset]);

  const handleCustomRange = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from && range?.to) {
        onDateRangeChange({ start: range.from, end: range.to });
      }
    },
    [onDateRangeChange],
  );

  const handlePresetSelection = useCallback(
    (presetValue: IntervalPreset) => {
      if (presetValue === "custom") {
        shouldOpenPickerRef.current = true;
        onPresetChange("custom");
      } else {
        shouldOpenPickerRef.current = false;
        onPresetChange(presetValue);
      }
    },
    [onPresetChange],
  );

  return (
    <div
      className="flex w-full items-center justify-between pt-2"
      data-testid="chart-top-filters"
    >
      <div
        className="flex items-center gap-2"
        data-testid="filter-controls-left"
      >
        <Menubar className="border-none p-0 shadow-none" data-testid="menubar">
          <MenubarMenu>
            <MenubarTrigger asChild>
              <IntervalSelector
                selectedPreset={selectedPreset}
                onPresetChange={handlePresetSelection}
                className="rounded-r-none border-1"
              />
            </MenubarTrigger>
          </MenubarMenu>

          <MenubarSeparator className="border-xs h-full" />

          <DatePickerWithRange
            ref={datePickerRef}
            date={{ from: dateRange.start, to: dateRange.end }}
            setDate={handleCustomRange}
            onApply={handleCustomRange}
            variant="ghost"
            placeholder={t("chart.dateRange.placeholder", "Select date range")}
            maxDate={new Date()}
            showApplyButton={true}
            preventAutoClose={selectedPreset === "custom"}
            data-testid="date-picker"
            buttonClassName="rounded-l-none border-1"
          />
        </Menubar>

        <p
          className="text-sm text-muted-foreground"
          data-testid="compare-to-label"
        >
          {t("chart.compareTo.label", "Compare to")}
        </p>

        <CompareToSelector
          compareTo={compareTo}
          onCompareToChange={onCompareToChange}
        />
      </div>

      <div
        className="flex items-center gap-2"
        data-testid="filter-controls-right"
      >
        <DataIntervalSelector
          interval={interval}
          onIntervalChange={onIntervalChange}
        />

        <FilterButton filtersCount={filtersCount} />
      </div>
    </div>
  );
};
